import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import jwtDecode from 'jwt-decode';

import { environment } from 'src/environments/environment';
import { CurrentUser, UserRoles } from './current-user';
import { TokenPayload } from './token-payload';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _loginUrl: string = environment.apiUrl + '/auth/login';
  private _isAdmin = new BehaviorSubject<boolean>(false);
  private _isUser = new BehaviorSubject<boolean>(false);
  private _currentUser: CurrentUser | undefined;

  public redirectionUrl: string = '/';

  constructor(private http: HttpClient) {
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      this._currentUser = JSON.parse(currentUser);
      this.setUserStatus();
    }
  }

  public get currentUser() {
    return this._currentUser;
  }

  public get isAdmin(): Observable<boolean> {
    return this._isAdmin.asObservable();
  }

  public get isUser(): Observable<boolean> {
    return this._isUser.asObservable();
  }

  logout(redirectionUrl = '/') {
    this.redirectionUrl = redirectionUrl;
    this._isAdmin.next(false);
    this._isUser.next(false);
    this._currentUser = undefined;
    localStorage.removeItem('currentUser');
  }

  async login(username: string, password: string): Promise<void> {
    // Call API-Backend for auth
    const tokenResponse = await this.fetchToken(username, password);
    const tokenPayload = jwtDecode<TokenPayload>(tokenResponse);
    // console.log('Token', tokenResponse, tokenPayload);
    this._currentUser = {
      name: username,
      token: tokenResponse,
      roles: this.mapRoles(tokenPayload),
    };
    // Save in LocalStorage for reload or other tabs
    localStorage.setItem('currentUser', JSON.stringify(this._currentUser));

    this.setUserStatus();
  }

  private setUserStatus() {
    this._isAdmin.next(
      this._currentUser?.roles.includes(UserRoles.admin) || false
    );
    this._isUser.next(
      this._currentUser?.roles.includes(UserRoles.user) || false
    );
  }

  private async fetchToken(
    username: string,
    password: string
  ): Promise<string> {
    const tokenResponse = await fetch(this._loginUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    if (!tokenResponse.ok) {
      throw Error(tokenResponse.statusText);
    }
    const response = await tokenResponse.json();
    return response.access_token;
  }

  private mapRoles(tokenPayload: TokenPayload): UserRoles[] {
    return tokenPayload.roles.map((role) => {
      switch (role) {
        case 'admin':
          return UserRoles.admin;
        case 'user':
          return UserRoles.user;
        default:
          return UserRoles.none;
      }
    });
  }
}
